import type { QueueStatus } from '../../types/jobTracking'

interface QueueStatusPanelProps {
  queueStatus: QueueStatus | null
  isLoading?: boolean
}

export default function QueueStatusPanel({ queueStatus, isLoading }: QueueStatusPanelProps) {
  return (
    <div className="p-3 border-b border-qcloud-border">
      <h4 className="text-xs font-semibold text-qcloud-text mb-2 uppercase tracking-wider">Queue Status</h4>
      {queueStatus ? (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-qcloud-muted">Running</span>
            <span className={`font-mono font-medium ${queueStatus.totalRunning > 0 ? 'text-green-600' : 'text-qcloud-muted'}`}>
              {queueStatus.totalRunning}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-qcloud-muted">Queued</span>
            <span className={`font-mono font-medium ${queueStatus.totalQueued > 0 ? 'text-amber-600' : 'text-qcloud-muted'}`}>
              {queueStatus.totalQueued}
            </span>
          </div>
          {queueStatus.estimatedWaitMinutes > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-qcloud-muted">Est. wait</span>
              <span className="font-mono text-qcloud-muted">{queueStatus.estimatedWaitMinutes.toFixed(0)} min</span>
            </div>
          )}

          {/* Running jobs */}
          {queueStatus.running.length > 0 && (
            <div className="mt-2 space-y-1">
              {queueStatus.running.map(job => (
                <div key={job.id} className="flex items-center gap-1.5 text-[10px] bg-green-50 rounded px-2 py-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                  <span className="font-mono truncate">{job.id.slice(0, 8)}</span>
                  <span className="text-green-600 font-medium ml-auto">{job.backend}</span>
                </div>
              ))}
            </div>
          )}

          {/* My jobs */}
          {queueStatus.myJobs.length > 0 && (
            <div className="mt-2">
              <div className="text-[9px] text-qcloud-muted uppercase tracking-wider mb-1">Your Jobs</div>
              {queueStatus.myJobs.map(job => (
                <div key={job.id} className="flex items-center justify-between text-[10px] py-0.5">
                  <span className="font-mono truncate">{job.id.slice(0, 8)}</span>
                  <span className={`font-medium ${
                    job.status === 'completed' ? 'text-green-600' :
                    job.status === 'failed' ? 'text-red-500' :
                    job.status === 'running' ? 'text-blue-600' :
                    'text-amber-600'
                  }`}>{job.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-[10px] text-qcloud-muted">
          {isLoading ? 'Loading...' : 'No queue data'}
        </div>
      )}
    </div>
  )
}
